import { useState, useEffect } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  type PaginationState,
} from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'
import { useCronEvents } from '../hooks/useCronEvents'
import type { CronEventLog, CronEventType, CronJobName } from '../types'
import {
  EVENT_TYPE_LABELS,
  EVENT_TYPE_COLORS,
  JOB_NAME_LABELS,
} from '../types'

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('es-CL', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function renderDetail(event: CronEventLog): string {
  const d = event.detail
  if (!d) return '—'

  switch (event.event_type) {
    case 'benefit_granted':
      return `${d.rule_name ?? ''} (${d.action_type ?? ''})`
    case 'benefit_expired':
      return `${d.action_type ?? ''} — expiró ${d.expired_at ?? ''}`
    case 'voucher_expired':
      return `${d.voucher_code ?? ''} — válido hasta ${d.valid_until ?? ''}`
    case 'notification_sent':
      return `${d.rule_name ?? d.voucher_code ?? ''} — vence ${d.expires ?? ''}`
    case 'category_upgrade':
    case 'category_downgrade':
      return `${d.old_category ?? '—'} → ${d.new_category ?? '—'}`
    case 'benefit_cancelled':
      return `${d.count ?? 0} cancelado(s) por ${d.reason === 'category_upgrade' ? 'upgrade' : 'downgrade'}`
    case 'error':
      return String(d.error ?? '')
    default:
      return JSON.stringify(d)
  }
}

const columnHelper = createColumnHelper<CronEventLog>()

const columns = [
  columnHelper.accessor('event_type', {
    header: 'Tipo',
    size: 160,
    cell: ({ getValue }) => {
      const type = getValue()
      const colors = EVENT_TYPE_COLORS[type]
      return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${colors.bg} ${colors.text}`}>
          {EVENT_TYPE_LABELS[type]}
        </span>
      )
    },
  }),
  columnHelper.accessor('job_name', {
    header: 'Job',
    size: 120,
    cell: ({ getValue }) => JOB_NAME_LABELS[getValue()] ?? getValue(),
  }),
  columnHelper.accessor('id_client', {
    header: 'Cliente',
    size: 80,
    cell: ({ getValue }) => {
      const id = getValue()
      if (id === null) return <span className="text-gray-400">—</span>
      return <span className="text-blue-600 font-medium">#{id}</span>
    },
  }),
  columnHelper.accessor('entity_type', {
    header: 'Entidad',
    size: 140,
    cell: ({ row }) => {
      const { entity_type, entity_id } = row.original
      if (!entity_type) return <span className="text-gray-400">—</span>
      return (
        <span className="text-gray-600 text-xs">
          {entity_type} #{entity_id}
        </span>
      )
    },
  }),
  columnHelper.display({
    id: 'detail',
    header: 'Detalle',
    cell: ({ row }) => {
      const text = renderDetail(row.original)
      const isError = row.original.event_type === 'error'
      return (
        <span className={`text-xs ${isError ? 'text-red-600' : 'text-gray-700'}`} title={text}>
          {text.length > 80 ? `${text.slice(0, 80)}…` : text}
        </span>
      )
    },
  }),
  columnHelper.accessor('created_at', {
    header: 'Hora',
    size: 80,
    cell: ({ getValue }) => formatTime(getValue()),
  }),
]

interface CronEventsListProps {
  executionId: number
}

export function CronEventsList({ executionId }: CronEventsListProps) {
  const navigate = useNavigate()
  const {
    data,
    total,
    isLoading,
    error,
    filters,
    setPage,
    setEventTypeFilter,
    setJobNameFilter,
    clearFilters,
  } = useCronEvents(executionId)

  const [localEventType, setLocalEventType] = useState<string>(filters.event_type ?? '')
  const [localJobName, setLocalJobName] = useState<string>(filters.job_name ?? '')

  useEffect(() => {
    setLocalEventType(filters.event_type ?? '')
    setLocalJobName(filters.job_name ?? '')
  }, [filters.event_type, filters.job_name])

  const hasFilters = filters.event_type !== undefined || filters.job_name !== undefined

  const handleApply = () => {
    setEventTypeFilter((localEventType || undefined) as CronEventType | undefined)
    setJobNameFilter((localJobName || undefined) as CronJobName | undefined)
  }

  const pagination: PaginationState = {
    pageIndex: filters.page - 1,
    pageSize: filters.limit,
  }

  const pageCount = Math.ceil(total / filters.limit)

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: { pagination },
    onPaginationChange: (updater) => {
      const newPagination =
        typeof updater === 'function' ? updater(pagination) : updater
      if (newPagination.pageIndex + 1 !== filters.page) {
        setPage(newPagination.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  })

  if (isLoading) {
    return <div className="animate-pulse h-48 bg-gray-100 rounded-lg" />
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
        {error}
      </div>
    )
  }

  return (
    <div>
      {/* Filter bar */}
      <div className="p-4 border-b flex flex-wrap items-center gap-3">
        <select
          value={localEventType}
          onChange={(e) => setLocalEventType(e.target.value)}
          className="input w-auto text-sm"
        >
          <option value="">Tipo: Todos</option>
          {Object.entries(EVENT_TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <select
          value={localJobName}
          onChange={(e) => setLocalJobName(e.target.value)}
          className="input w-auto text-sm"
        >
          <option value="">Job: Todos</option>
          {Object.entries(JOB_NAME_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <button onClick={handleApply} className="btn btn-primary text-sm">
          Aplicar
        </button>

        {hasFilters && (
          <button onClick={clearFilters} className="btn btn-secondary text-sm">
            Limpiar
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50 border-b">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className="px-4 py-3 font-medium text-gray-700">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center text-gray-500">
                  No se encontraron eventos
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`border-b hover:bg-gray-50 transition-colors ${
                    row.original.id_client ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => {
                    if (row.original.id_client) {
                      navigate(`/clients/${row.original.id_client}`)
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > filters.limit && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <span className="text-sm text-gray-500">
            {total} evento{total !== 1 ? 's' : ''} - Página {filters.page} de {pageCount || 1}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(filters.page - 1)}
              disabled={filters.page <= 1}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage(filters.page + 1)}
              disabled={filters.page >= pageCount}
              className="btn btn-secondary text-sm disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
